<template>
  <ContainerBasic
    class="touch:md:px-0 lg:px-0"
    :container-type="blockData.containerType"
  >
    <Slider :show-slides="1">
      <div v-for="(sliderPerson, index) in blockData.persons" :key="index">
        <QuoteFrame
          :style="blockData.bgStyle.style + blockData.textStyle.style"
          :outer-classes="outerClasses"
          :inner-display-class="innerDisplayClass"
          :quotes-bg-style="blockData.quotesBGStyle.style"
          :has-quotes="!!blockData.quote"
        >
          <template #quote>
            <p
              v-if="props.blockData.quote || (sliderPerson.description ?? '')"
              class="italic"
              :class="props.blockData.quote ? 'ml-md mr-md md:mr-0' : ''"
              :style="blockData.textStyle.style"
            >
              {{ props.blockData.quote || (sliderPerson.description ?? '') }}
            </p>
            <p v-if="blockData.rating">
              <FaIcon
                v-for="idx in maxRating"
                :key="idx"
                icon-class="fas fa-star"
                :classes="
                  idx < blockData.rating
                    ? 'mr-xs text-vdv-base mt-md'
                    : 'mr-xs text-vdv-border-light mt-md'
                "
              />
            </p>
            <p v-if="blockData.showVMVLogo" class="text-center mt-md">
              <img
                src="@/assets/images/vdv_menschzumensch_claim.svg"
                class="w-[210px] mx-auto"
                :style="
                  blockData.textStyle.themeType === 'BASE'
                    ? 'filter: brightness(100)'
                    : ''
                "
                loading="lazy"
              />
            </p>
            <div v-if="blockData.showLink" class="text-center mt-md">
              <LinkBasic
                :classes="
                  blockData.textStyle.style
                    ? blockData.textStyle.themeType === 'BASE'
                      ? 'text-activesection-font-base'
                      : 'text-vdv-base'
                    : 'text-vdv-base'
                "
                :link="sliderPerson.slug"
                :label="
                  t('personTile.linkKnowMore', {
                    personData: `${sliderPerson.title} ${
                      sliderPerson.firstName ?? ''
                    } ${sliderPerson.lastName ?? ''}`,
                  })
                "
                icon="fas fa-chevron-right"
              />
            </div>
          </template>
          <template #avatar>
            <AvatarIcon
              v-if="sliderPerson.image?.desktop?.url != ''"
              :size="EAvatarSize.lg"
              :image="sliderPerson.image?.desktop?.url"
            />
            <AvatarIcon v-else :size="EAvatarSize.lg" />
            <div
              class="flex flex-col w-full"
              :style="blockData.textStyle.style"
            >
              <div class="font-bold">
                <span>{{ getSalutation(sliderPerson) }}</span>
              </div>
              <div>
                <div
                  v-if="sliderPerson.jobPosition || sliderPerson.profession"
                  class="mb-0 touch:md:mb-sm lg:mb-sm"
                >
                  <span v-if="sliderPerson.jobPosition"
                    >{{ sliderPerson.jobPosition }}&nbsp;</span
                  >
                  <span
                    v-if="sliderPerson.profession && sliderPerson.jobPosition"
                    >&nbsp;|&nbsp;</span
                  >
                  <span v-if="sliderPerson.profession">{{
                    sliderPerson.profession
                  }}</span>
                </div>
                <div
                  class="flex flex-row flex-wrap justify-center touch:md:justify-start lg:justify-start"
                >
                  <div
                    v-if="sliderPerson.phone && blockData.showContactData"
                    class="flex flex-row items-center justify-center w-full touch:md:justify-start lg:justify-start"
                  >
                    <FaIcon icon-class="fal fa-phone" classes="mr-xs" />
                    <a
                      :href="`tel:${sliderPerson.phone}`"
                      class="flex text-left flex-nowrap whitespace-nowrap w-[150px]"
                      >{{ sliderPerson.phone }}</a
                    >
                  </div>
                  <div v-if="sliderPerson.email && blockData.showContactData">
                    <PersonMail
                      :mail="sliderPerson.email"
                      :is-bold="true"
                      :has-width="true"
                      :has-position-lg="true"
                      :has-text-centered="true"
                      :has-text-color="blockData.textStyle.style"
                    />
                  </div>
                </div>
              </div>
            </div>
          </template>
        </QuoteFrame>
      </div>
    </Slider>
    <span class="hidden border-dotted border-y-2"></span>
  </ContainerBasic>
</template>

<script setup lang="ts">
import { EAvatarSize } from '@/@types/avatar-icon';
import ContainerBasic from '@/components/components/container-basic.vue';
import AvatarIcon from '@/components/components/core/avatar-icon.vue';
import LinkBasic from '@/components/components/core/basicLink/basic-link.vue';
import PersonMail from '@/components/components/person-mail.vue';
import type { PersonBlockData } from '../blockType';
import type { PersonData } from '~~/src/server/transformers/EntityTransformers/personTransformer';
import FaIcon from '@/components/fa-icon.vue';
import Slider from '@/components/slider/slider.vue';
import QuoteFrame from '../../components/quoteFrame/vdv/quoteFrame.vue';

const props = defineProps({
  blockData: {
    type: Object as PropType<PersonBlockData>,
    required: true,
    default: {} as PersonBlockData,
  },
});

const { t } = useTrans();

const maxRating = 5;

// const wrapperDisplayClass = ref('flex-col-reverse lg:flex-row');
const innerDisplayClass = ref(
  'flex-col place-items-center text-center touch:md:flex-row touch:md:text-left lg:flex-row lg:text-left',
);

const outerClasses = computed(() => {
  let classes = 'px-md h-full';
  const hasDottedBorder = props.blockData.personStyle === 'dottedBorder';
  const hasDottedBorderTop = props.blockData.personStyle === 'dottedBorderTop';
  const hasBg = props.blockData.bgStyle.style !== '';
  const hasQuote = props.blockData.quote;
  if (hasBg) classes += '!px-sm ';
  if (hasDottedBorder) classes += ' border-y-2 border-dotted';
  if (hasDottedBorderTop) classes += ' border-t-2 border-dotted';
  if (hasBg || hasDottedBorder || hasDottedBorderTop || hasQuote)
    classes += ' py-lg';

  return classes;
});

function getSalutation(person: PersonData) {
  return person.title
    ? `${person.title} ${person.firstName} ${person.lastName}`
    : `${person.firstName} ${person.lastName}`;
}
/**
 * @todo
Not implemented in Craft yet
*/

//if (props.section._context.rightToLeft) {
//  wrapperDisplayClass.value = 'flex-row-reverse';
//}

//if (props.section._context.isSmall) {
//  wrapperDisplayClass.value = '!flex-col-reverse !md:flex-col-reverse';
//  innerDisplayClass.value = '!flex-col !place-items-center !text-center';
//}
</script>

<style scoped lang="postcss"></style>
